import React, { createContext, useState, useContext, useEffect } from 'react';
import { assessmentsData } from '../data/assessments';
import { getFrameworkMappingForCheck } from '../data/frameworkMappings';

// Create context
const CloudSecurityContext = createContext();

// Custom hook to use the context
export const useCloudSecurity = () => useContext(CloudSecurityContext);

export const CloudSecurityProvider = ({ children }) => {
  const [currentProvider, setCurrentProvider] = useState(null);
  const [assessmentResults, setAssessmentResults] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState('All');

  // Central gate (CISO Marketplace): gated UP FRONT — selecting a provider starts the assessment, which
  // triggers requireRun (member-free / credits / Tools plan / one-time guest pay). gate.js shows the overlay.
  const [started, setStarted] = useState(false);
  const ensureStarted = async () => {
    if (started) return true;
    const g = window.csmGate;
    const ok = g?.requireRun ? await g.requireRun() : true; // graceful: no gate → allow
    if (ok) setStarted(true);
    return ok;
  };
  // Restore progress + resume after a guest-pay / sign-in bounce.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cloudscan_state') || 'null');
      if (saved) {
        if (saved.currentProvider) setCurrentProvider(saved.currentProvider);
        if (saved.assessmentResults) setAssessmentResults(saved.assessmentResults);
      }
    } catch { /* ignore */ }
    const g = window.csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && g?.requireRun) {
      g.requireRun().then((ok) => { if (ok) setStarted(true); });
    }
  }, []);
  // Persist progress so the pay/login redirect doesn't lose it.
  useEffect(() => {
    try { localStorage.setItem('cloudscan_state', JSON.stringify({ currentProvider, assessmentResults })); } catch { /* ignore */ }
  }, [currentProvider, assessmentResults]);
  // Save the report to the member portal's Reports tab when results are revealed (logged-in only).
  useEffect(() => {
    if (!showResults) return;
    const g = window.csmGate;
    if (g?.isLoggedIn?.() && g.saveReport) {
      const score = calculateOverallScore();
      g.saveReport({ title: 'Cloud Security Assessment', summary: `cloud posture — ${score.percentage}%`, data: { currentProvider, assessmentResults, score } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults]);

  // Select provider (gated up front)
  const selectProvider = async (provider) => {
    if (!(await ensureStarted())) return; // gate overlay shown — wait until entitled
    setCurrentProvider(provider);
    setAssessmentResults({});
    setShowResults(false);
  };

  // Update the score for a specific check
  const toggleCheck = (catIndex, checkIndex, isChecked) => {
    const newResults = { ...assessmentResults };
    
    if (!newResults[catIndex]) {
      newResults[catIndex] = {};
    }
    
    newResults[catIndex][checkIndex] = isChecked;
    
    setAssessmentResults(newResults);
  };

  // Calculate the score for a category
  const calculateCategoryScore = (catIndex) => {
    const assessment = assessmentsData[currentProvider];
    if (!assessment) return { checked: 0, total: 0 };
    
    const category = assessment.categories[catIndex];
    let checkedCount = 0;
    
    category.checks.forEach((_, checkIndex) => {
      if (assessmentResults[catIndex] && assessmentResults[catIndex][checkIndex]) {
        checkedCount++;
      }
    });
    
    return {
      checked: checkedCount,
      total: category.checks.length,
      percentage: category.checks.length > 0 ? (checkedCount / category.checks.length) * 100 : 0
    };
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    const assessment = assessmentsData[currentProvider];
    if (!assessment) return { checked: 0, total: 0, percentage: 0 };
    
    let totalChecked = 0;
    let totalChecks = 0;
    
    assessment.categories.forEach((category, catIndex) => {
      category.checks.forEach((_, checkIndex) => {
        totalChecks++;
        if (assessmentResults[catIndex] && assessmentResults[catIndex][checkIndex]) {
          totalChecked++;
        }
      });
    });
    
    return {
      checked: totalChecked,
      total: totalChecks,
      percentage: totalChecks > 0 ? Math.round((totalChecked / totalChecks) * 100) : 0
    };
  };

  // Generate recommendations
  const generateRecommendations = () => {
    const assessment = assessmentsData[currentProvider];
    if (!assessment) return [];
    
    const recommendations = [];
    
    assessment.categories.forEach((category, catIndex) => {
      category.checks.forEach((check, checkIndex) => {
        if ((!assessmentResults[catIndex] || !assessmentResults[catIndex][checkIndex]) && check.risk === 'high') {
          recommendations.push({
            category: category.name,
            check: check.title,
            description: check.description
          });
        }
      });
    });
    
    return recommendations;
  };

  // Generate downloadable report
  const generateReport = () => {
    const assessment = assessmentsData[currentProvider];
    if (!assessment) return '';
    
    let report = `CLOUD SECURITY ASSESSMENT REPORT\n`;
    report += `Provider: ${assessment.name}\n`;
    report += `Date: ${new Date().toLocaleDateString()}\n\n`;
    report += `Overall Score: ${calculateOverallScore().percentage}%\n\n`;
    
    assessment.categories.forEach((category, catIndex) => {
      report += `\n${category.name}\n${'='.repeat(category.name.length)}\n`;
      
      category.checks.forEach((check, checkIndex) => {
        const status = (assessmentResults[catIndex] && assessmentResults[catIndex][checkIndex]) ? '✓' : '✗';
        report += `${status} ${check.title}\n   ${check.description} [${check.risk.toUpperCase()} RISK]\n`;
        
        // Add framework mappings if available
        const mappings = getFrameworkMappingForCheck(check.title);
        if (Object.keys(mappings).length > 0) {
          report += `   Framework Mappings:\n`;
          Object.entries(mappings).forEach(([framework, controls]) => {
            report += `   - ${framework}: ${controls}\n`;
          });
        }
        
        report += '\n';
      });
    });
    
    return report;
  };

  return (
    <CloudSecurityContext.Provider
      value={{
        currentProvider,
        assessmentResults,
        showResults,
        assessmentsData,
        selectedFramework,
        selectProvider,
        toggleCheck,
        calculateCategoryScore,
        calculateOverallScore,
        generateRecommendations,
        generateReport,
        setShowResults,
        setSelectedFramework
      }}
    >
      {children}
    </CloudSecurityContext.Provider>
  );
};

export default CloudSecurityContext; 